import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, Trash2, Fuel, X, Loader2 } from 'lucide-react';
import { API } from '../App';

const DELIVERY_TERMS = ['FOB', 'CIF', 'CFR', 'TTO', 'TTT', 'EXW', 'DES'];
const PAYMENT_TERMS = ['SBLC', 'MT103', 'DLC', 'LC', 'Escrow'];

// Common commodity presets — one click fills the form with realistic starter data.
const PRESETS = {
  BLCO: {
    name: 'Bonny Light Crude Oil (BLCO)',
    subcategory: 'Crude Oil',
    origin: 'Nigeria',
    description:
      'Bonny Light Crude Oil — light, sweet crude with low sulphur content. Ideal feedstock for gasoline and middle distillates. Loading port: Bonny Terminal, Nigeria.',
    delivery_terms: ['FOB', 'CIF', 'TTO'],
    payment_terms: ['SBLC', 'MT103', 'DLC'],
    min_quantity: '1,000,000 barrels/month (12-month contract available)',
    indicative_price_unit: 'USD/barrel',
    spec_sheet: {
      'API Gravity': '32.9°',
      Sulphur: '0.14%',
      'Pour Point': '-15°C',
      'Flash Point': '<25°C',
      'Viscosity @ 40°C': '3.5 cSt',
      Origin: 'Bonny Terminal, Nigeria',
    },
    image_hint: 'https://images.unsplash.com/photo-1518553552028-7a1041ac72b8?w=900&q=80',
  },
  EN590: {
    name: 'EN590 10ppm ULSD (Diesel)',
    subcategory: 'Diesel',
    origin: 'Rotterdam ARA / Fujairah',
    description:
      'EN590 Ultra-Low-Sulphur Diesel (10ppm) meeting European automotive spec. Available ex-tank Rotterdam ARA and Fujairah. SGS inspection at loading.',
    delivery_terms: ['CIF', 'FOB'],
    payment_terms: ['SBLC', 'MT103', 'DLC'],
    min_quantity: '50,000 MT / month',
    indicative_price_unit: 'USD/MT',
    spec_sheet: {
      Sulphur: '≤ 10 mg/kg',
      Density: '820 – 845 kg/m³ @ 15°C',
      'Cetane Number': '≥ 51',
      'Flash Point': '≥ 55°C',
      'Pour Point': '≤ -12°C',
      'Water Content': '≤ 200 mg/kg',
    },
    image_hint: 'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=900&q=80',
  },
  JETA1: {
    name: 'Jet A1 Aviation Fuel',
    subcategory: 'Aviation Fuel',
    origin: 'Rotterdam / Houston / Fujairah',
    description:
      'Jet A1 aviation kerosene meeting ASTM D1655 and DEF STAN 91-091. Ex-tank major hubs. Independent inspection (SGS / Intertek) at loading port.',
    delivery_terms: ['CIF', 'FOB'],
    payment_terms: ['SBLC', 'MT103', 'DLC'],
    min_quantity: '100,000 MT / month',
    indicative_price_unit: 'USD/MT',
    spec_sheet: {
      Specification: 'ASTM D1655 / DEF STAN 91-091',
      'Flash Point': '≥ 38°C',
      'Freezing Point': '≤ -47°C',
      Density: '775 – 840 kg/m³ @ 15°C',
      Sulphur: '≤ 0.3 mass %',
      Colour: 'Saybolt +25 min',
    },
    image_hint: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=900&q=80',
  },
  LNG: {
    name: 'Liquefied Natural Gas (LNG)',
    subcategory: 'LNG',
    origin: 'Nigeria LNG / Qatar / USA',
    description:
      'LNG cargo, market-standard spec. Delivery ex-shipping under CIF, DES, or FOB Incoterms. Bank-instrument settlement only.',
    delivery_terms: ['CIF', 'FOB', 'DES'],
    payment_terms: ['SBLC', 'DLC', 'MT103'],
    min_quantity: '1 – 12 cargoes / year (approx 65,000 MT/cargo)',
    indicative_price_unit: 'USD/MMBtu',
    spec_sheet: {
      Methane: '≥ 87 mol%',
      Ethane: '≤ 8 mol%',
      'Higher Heating Value': '1,050 – 1,120 BTU/scf',
      'Sulphur Content': '≤ 4 mg/Nm³',
      'CO₂': '≤ 50 ppm',
      Nitrogen: '≤ 1 mol%',
    },
    image_hint: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=900&q=80',
  },
};

const initialSpecRow = () => ({ id: Math.random().toString(36).slice(2, 9), key: '', value: '' });

export default function CommodityProductAdmin() {
  const [form, setForm] = useState({
    name: '',
    subcategory: '',
    origin: '',
    description: '',
    category: 'Oil & Gas',
    indicative_price: '',
    indicative_price_unit: '',
    min_quantity: '',
    imageUrl: '',
  });
  const [images, setImages] = useState([]);
  const [specRows, setSpecRows] = useState([initialSpecRow(), initialSpecRow(), initialSpecRow()]);
  const [delivery, setDelivery] = useState([]);
  const [payment, setPayment] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const loadList = useCallback(async () => {
    try {
      setLoadingList(true);
      const r = await axios.get(`${API}/admin/commodity-products`);
      setList(r.data?.products || []);
    } catch (_e) {
      setList([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const applyPreset = (key) => {
    const p = PRESETS[key];
    if (!p) return;
    setForm((f) => ({
      ...f,
      name: p.name,
      subcategory: p.subcategory || '',
      origin: p.origin || '',
      description: p.description || '',
      indicative_price_unit: p.indicative_price_unit || '',
      min_quantity: p.min_quantity || '',
    }));
    setSpecRows(
      Object.entries(p.spec_sheet).map(([k, v]) => ({
        id: Math.random().toString(36).slice(2, 9),
        key: k,
        value: v,
      })),
    );
    setDelivery(p.delivery_terms || []);
    setPayment(p.payment_terms || []);
    if (p.image_hint && images.length === 0) setImages([p.image_hint]);
    toast.success(`Preset "${p.name}" loaded — edit before saving.`);
  };

  const toggleTerm = (list_, term, setter) => {
    setter(list_.includes(term) ? list_.filter((t) => t !== term) : [...list_, term]);
  };

  const addSpecRow = () => setSpecRows((rows) => [...rows, initialSpecRow()]);
  const removeSpecRow = (id) => setSpecRows((rows) => rows.filter((r) => r.id !== id));
  const updateSpecRow = (id, field, val) =>
    setSpecRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: val } : r)));

  const addImage = () => {
    const url = form.imageUrl.trim();
    if (!url) return;
    if (!url.startsWith('http')) {
      toast.error('Image URL must start with http/https');
      return;
    }
    setImages((im) => [...im, url].slice(0, 6));
    setForm((f) => ({ ...f, imageUrl: '' }));
  };
  const removeImage = (idx) => setImages((im) => im.filter((_v, i) => i !== idx));

  const resetForm = () => {
    setForm({ name: '', subcategory: '', origin: '', description: '', category: 'Oil & Gas', indicative_price: '', indicative_price_unit: '', min_quantity: '', imageUrl: '' });
    setImages([]);
    setSpecRows([initialSpecRow(), initialSpecRow(), initialSpecRow()]);
    setDelivery([]);
    setPayment([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) {
      toast.error('Name and description are required.');
      return;
    }
    const spec_sheet = {};
    specRows.forEach((r) => {
      const k = r.key.trim();
      const v = r.value.trim();
      if (k && v) spec_sheet[k] = v;
    });
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      subcategory: form.subcategory.trim() || null,
      origin: form.origin.trim() || null,
      category: form.category.trim() || 'Oil & Gas',
      images,
      spec_sheet,
      delivery_terms: delivery,
      payment_terms: payment,
      indicative_price: form.indicative_price ? Number(form.indicative_price) : null,
      indicative_price_unit: form.indicative_price_unit.trim() || null,
      min_quantity: form.min_quantity.trim() || null,
    };
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/commodity-products`, payload);
      toast.success(`Commodity created: ${r.data?.name}`);
      resetForm();
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create commodity product.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId, name) => {
    if (!window.confirm(`Delete "${name}"? This removes it from the /oil-and-gas page and product catalog.`)) return;
    try {
      await axios.delete(`${API}/admin/commodity-products/${productId}`);
      toast.success('Commodity product deleted');
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete.');
    }
  };

  return (
    <div className="space-y-6" data-testid="commodity-admin">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fuel className="h-5 w-5" /> Create Commodity Product
          </CardTitle>
          <CardDescription>
            Add a market-priced commodity (Crude Oil, EN590, Jet A1, LNG, etc.) to the
            Energy Trading Desk. It shows immediately on the <code>/oil-and-gas</code> landing page and
            the main product catalog with a Request-Quote CTA.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Presets */}
          <div className="mb-6">
            <label className="text-sm font-medium block mb-2">Start from a preset (optional)</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PRESETS).map(([k, p]) => (
                <Button
                  key={k}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(k)}
                  data-testid={`commodity-preset-${k.toLowerCase()}`}
                >
                  {p.name}
                </Button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Product name *</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Bonny Light Crude Oil (BLCO)"
                  data-testid="commodity-name"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Subcategory / grade</label>
                <Input
                  value={form.subcategory}
                  onChange={(e) => setForm((f) => ({ ...f, subcategory: e.target.value }))}
                  placeholder="Crude Oil"
                  data-testid="commodity-subcategory"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Origin / loading port</label>
                <Input
                  value={form.origin}
                  onChange={(e) => setForm((f) => ({ ...f, origin: e.target.value }))}
                  placeholder="Bonny Terminal, Nigeria"
                  data-testid="commodity-origin"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Category</label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="Oil & Gas"
                  data-testid="commodity-category"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Description *</label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Short public-facing description — origin, spec class, loading terms, inspection."
                data-testid="commodity-description"
                required
              />
            </div>

            {/* Images */}
            <div>
              <label className="text-sm font-medium block mb-1">Images (paste URL, up to 6)</label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={form.imageUrl}
                  onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                  placeholder="https://..."
                  data-testid="commodity-image-url"
                />
                <Button type="button" variant="outline" onClick={addImage} data-testid="commodity-image-add">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="commodity-image-list">
                  {images.map((url, i) => (
                    <div key={url} className="relative w-24 h-24 rounded-md overflow-hidden border border-border group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                        data-testid={`commodity-image-remove-${i}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Spec Sheet */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Spec sheet (key / value)</label>
                <Button type="button" variant="outline" size="sm" onClick={addSpecRow} data-testid="commodity-spec-add">
                  <Plus className="h-4 w-4 mr-1" /> Add row
                </Button>
              </div>
              <div className="space-y-2" data-testid="commodity-spec-rows">
                {specRows.map((r, idx) => (
                  <div key={r.id} className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-center">
                    <Input
                      value={r.key}
                      onChange={(e) => updateSpecRow(r.id, 'key', e.target.value)}
                      placeholder="Sulphur"
                      data-testid={`commodity-spec-key-${idx}`}
                    />
                    <Input
                      value={r.value}
                      onChange={(e) => updateSpecRow(r.id, 'value', e.target.value)}
                      placeholder="0.05%"
                      data-testid={`commodity-spec-value-${idx}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSpecRow(r.id)}
                      disabled={specRows.length <= 1}
                      data-testid={`commodity-spec-remove-${idx}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery + Payment terms */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium block mb-2">Delivery terms (Incoterms)</label>
                <div className="flex flex-wrap gap-2">
                  {DELIVERY_TERMS.map((t) => {
                    const active = delivery.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTerm(delivery, t, setDelivery)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                        }`}
                        data-testid={`commodity-delivery-${t.toLowerCase()}`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Payment instruments</label>
                <div className="flex flex-wrap gap-2">
                  {PAYMENT_TERMS.map((t) => {
                    const active = payment.includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => toggleTerm(payment, t, setPayment)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                        }`}
                        data-testid={`commodity-payment-${t.toLowerCase()}`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Indicative pricing */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium block mb-1">Indicative price (optional)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.indicative_price}
                  onChange={(e) => setForm((f) => ({ ...f, indicative_price: e.target.value }))}
                  placeholder="75.50"
                  data-testid="commodity-indicative-price"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Price unit</label>
                <Input
                  value={form.indicative_price_unit}
                  onChange={(e) => setForm((f) => ({ ...f, indicative_price_unit: e.target.value }))}
                  placeholder="USD/barrel"
                  data-testid="commodity-price-unit"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Minimum quantity</label>
                <Input
                  value={form.min_quantity}
                  onChange={(e) => setForm((f) => ({ ...f, min_quantity: e.target.value }))}
                  placeholder="1,000,000 barrels/month"
                  data-testid="commodity-min-quantity"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={resetForm} data-testid="commodity-reset">
                Reset
              </Button>
              <Button type="submit" disabled={submitting} data-testid="commodity-submit" size="lg">
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" /> Create commodity</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Existing commodity products */}
      <Card>
        <CardHeader>
          <CardTitle>Existing commodities</CardTitle>
          <CardDescription>All house-desk commodity listings currently live on the marketplace.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingList ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="commodity-list-empty">
              No commodity products yet. Use a preset above to seed your first listing.
            </div>
          ) : (
            <div className="space-y-3" data-testid="commodity-list">
              {list.map((p) => (
                <div
                  key={p.product_id}
                  className="flex items-center gap-4 p-3 rounded-lg border border-border bg-background"
                  data-testid={`commodity-row-${p.product_id}`}
                >
                  <img
                    src={(p.images && p.images[0]) || 'https://images.unsplash.com/photo-1518553552028-7a1041ac72b8?w=200'}
                    alt={p.name}
                    className="w-16 h-16 rounded-md object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[p.subcategory, p.origin].filter(Boolean).join(' · ') || p.category}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(p.delivery_terms || []).slice(0, 4).map((t) => (
                        <Badge key={`d-${t}`} variant="outline" className="text-xs">{t}</Badge>
                      ))}
                      {(p.payment_terms || []).slice(0, 4).map((t) => (
                        <Badge key={`p-${t}`} variant="secondary" className="text-xs">{t}</Badge>
                      ))}
                      {p.has_spec && <Badge className="text-xs">{Object.keys(p.spec_sheet || {}).length} spec</Badge>}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => handleDelete(p.product_id, p.name)}
                    data-testid={`commodity-delete-${p.product_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
